
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatCuivre = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Cuivre - Rachat Câbles, Tuyaux, Fils Électriques | Groupe Fabre"
        description="Rachat de cuivre au meilleur prix : câbles électriques, tuyauteries, fils dénudés. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/cuivre"
        keywords="achat cuivre, rachat câbles électriques, tuyaux cuivre, prix cuivre, cuivre dénudé"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Zap className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Cuivre</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de câbles, tuyaux et fils électriques</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de cuivre - Métal noble et conducteur
                </h2>
                <p className="text-gray-600 mb-6">
                  Le <strong>cuivre</strong> est l'un des métaux les plus valorisés sur le marché du recyclage. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types de cuivre : 
                  câbles électriques, tuyauteries, fils dénudés et déchets d'installation.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de cuivre rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-orange-500 text-white mr-3">•</span>
                    <span><strong>Cuivre nu :</strong> fils dénudés, barres, tubes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-orange-500 text-white mr-3">•</span>
                    <span><strong>Câbles électriques :</strong> avec gaine plastique</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-orange-500 text-white mr-3">•</span>
                    <span><strong>Tuyauteries :</strong> plomberie, chauffage, climatisation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-orange-500 text-white mr-3">•</span>
                    <span><strong>Chutes industrielles :</strong> tournage, usinage</span>
                  </li>
                </ul>

                <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
                  <h4 className="font-bold text-orange-800 mb-3">Propriétés du cuivre</h4>
                  <ul className="text-orange-700 text-sm space-y-2">
                    <li>• Excellente conductivité électrique et thermique</li>
                    <li>• Résistance naturelle à la corrosion</li>
                    <li>• Recyclable à 100% sans perte de qualité</li>
                    <li>• Valeur élevée et stable sur le marché</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Expertise cuivre</h3>
                <p className="text-gray-600 mb-6">
                  Notre expertise nous permet d'identifier les différentes qualités de cuivre 
                  et de vous proposer la meilleure valorisation selon la pureté du métal.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-orange-600 mr-3" />
                    <span>Prix cuivre au cours LME</span>
                  </div>
                  <div className="flex items-center">
                    <Zap className="h-5 w-5 text-orange-600 mr-3" />
                    <span>Test de pureté inclus</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-orange-600 mr-3" />
                    <span>Valorisation maximale</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-orange-600 text-white rounded-md font-medium hover:bg-orange-700 transition-colors">
                    Demander prix cuivre
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Classifications cuivre */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications cuivre et tarifs
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Cuivre Nu Bright</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Cuivre pur {'>'}99%, brillant, sans oxydation
                </p>
                <div className="text-green-600 font-medium">Prix le plus élevé</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Cuivre Nu Mill</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Cuivre pur avec légère oxydation
                </p>
                <div className="text-orange-600 font-medium">Prix premium</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Câbles Cuivre</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Cuivre avec isolation plastique
                </p>
                <div className="text-blue-600 font-medium">Prix selon % cuivre</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatCuivre;
